/**
 * Feature Flags Page
 */

import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Skeleton,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useFeatureFlags, useCreateFeatureFlag, useUpdateFeatureFlag, useDeleteFeatureFlag } from '../hooks';
import { useSnackbar } from 'notistack';
import { FlagOverridesDialog } from '../components/feature-flags/FlagOverridesDialog';

export function FeatureFlagsPage() {
  const { enqueueSnackbar } = useSnackbar();
  const { data: flags, isLoading } = useFeatureFlags();
  const createFlag = useCreateFeatureFlag();
  const updateFlag = useUpdateFeatureFlag();
  const deleteFlag = useDeleteFeatureFlag();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newFlag, setNewFlag] = useState({
    key: '',
    description: '',
    defaultValue: false,
    isCritical: false,
  });
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [overridesDialogFlagKey, setOverridesDialogFlagKey] = useState<
    string | null
  >(null);

  const handleCreate = async () => {
    try {
      await createFlag.mutateAsync(newFlag);
      enqueueSnackbar('Feature flag created', { variant: 'success' });
      setIsCreateOpen(false);
      setNewFlag({
        key: '',
        description: '',
        defaultValue: false,
        isCritical: false,
      });
    } catch {
      enqueueSnackbar('Failed to create feature flag', { variant: 'error' });
    }
  };

  const handleToggleDefault = async (key: string, currentValue: boolean) => {
    try {
      await updateFlag.mutateAsync({ key, dto: { defaultValue: !currentValue } });
      enqueueSnackbar('Default value updated', { variant: 'success' });
    } catch {
      enqueueSnackbar('Failed to update flag', { variant: 'error' });
    }
  };

  const handleDelete = async (key: string) => {
    try {
      await deleteFlag.mutateAsync(key);
      enqueueSnackbar('Feature flag deleted', { variant: 'success' });
      setDeleteConfirm(null);
    } catch {
      enqueueSnackbar('Failed to delete feature flag', { variant: 'error' });
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight={600}>
            Feature Flags
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage global feature flags and tenant overrides
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsCreateOpen(true)}
        >
          Add Flag
        </Button>
      </Box>

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Key</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="center">Default</TableCell>
                <TableCell align="center">Overrides</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                [...Array(4)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(5)].map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : flags?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      No feature flags defined
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                flags?.map((flag) => (
                  <TableRow key={flag.key}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography fontWeight={500} fontFamily="monospace">
                          {flag.key}
                        </Typography>
                        {flag.isCritical && (
                          <Chip
                            label="CRITICAL"
                            color="warning"
                            size="small"
                            icon={<WarningIcon fontSize="small" />}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {flag.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={flag.defaultValue}
                        onChange={() => handleToggleDefault(flag.key, flag.defaultValue)}
                        disabled={updateFlag.isPending}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={<PeopleIcon />}
                        label={`${flag.overrideCount ?? 0} tenants`}
                        size="small"
                        variant="outlined"
                        onClick={() => setOverridesDialogFlagKey(flag.key)}
                        sx={{ cursor: 'pointer' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteConfirm(flag.key)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Create Dialog */}
      <Dialog open={isCreateOpen} onClose={() => setIsCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Feature Flag</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Key"
            value={newFlag.key}
            onChange={(e) => setNewFlag((prev) => ({ ...prev, key: e.target.value }))}
            margin="normal"
            helperText="Use snake_case (e.g., new_feature_enabled)"
          />
          <TextField
            fullWidth
            label="Description"
            value={newFlag.description}
            onChange={(e) => setNewFlag((prev) => ({ ...prev, description: e.target.value }))}
            margin="normal"
            multiline
            rows={2}
          />
          <FormControlLabel
            control={
              <Switch
                checked={newFlag.defaultValue}
                onChange={(e) => setNewFlag((prev) => ({ ...prev, defaultValue: e.target.checked }))}
              />
            }
            label="Default enabled"
            sx={{ mt: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={newFlag.isCritical}
                onChange={(e) =>
                  setNewFlag((prev) => ({ ...prev, isCritical: e.target.checked }))
                }
              />
            }
            label="Mark as critical"
            sx={{ mt: 1 }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', ml: 4, mt: -0.5 }}
          >
            Critical flags require typed confirmation before any override change.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!newFlag.key || createFlag.isPending}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Feature Flag</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the feature flag "{deleteConfirm}"?
            This will also remove all tenant-specific overrides.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
            disabled={deleteFlag.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <FlagOverridesDialog
        open={!!overridesDialogFlagKey}
        flagKey={overridesDialogFlagKey ?? ''}
        onClose={() => setOverridesDialogFlagKey(null)}
      />
    </Box>
  );
}
